import React from 'react';
import { Tooltip } from 'antd';
import {
  LuXCircle,
  LuCrosshair,
  LuExpand,
  LuGitMerge,
  LuMagnet,
  LuPencil,
  LuPlus,
  LuRedo2,
  LuRuler,
  LuScissors,
  LuMinusSquare,
  LuTag,
  LuTrash2,
  LuUndo2,
  LuX,
} from 'react-icons/lu';
import { IconType } from 'react-icons';
import ShortcutHint, {
  CmdKey,
  ShortcutKey,
} from 'components/common/ShortcutHint/ShortcutHint';
import type { PaddockMapMode } from './PaddockMapCanvas';
import type { MapFeature } from '../types';

// ─── Design tokens (OpterraPhoenixWeb) ───────────────────────
// --color-success:      #00614f  (green — enabled icon / active border)
// --color-bright-gray:  #e5e9ec  (hover background, group separators)
// --color-argent-gray:  #c1c1c1  (disabled icon — WCAG exempts disabled controls)
// --color-danger:       #d10000  (delete icon, count badge — white text 5.9:1)
// --radius-button:      0.5rem   (8px → rounded-lg)

const ICON_SIZE = 16;

function ic(Icon: IconType, size = ICON_SIZE) {
  const C = Icon as React.ComponentType<{ size?: number }>;
  return <C size={size} />;
}

const BTN_BASE =
  'flex h-8 w-8 items-center justify-center rounded-[var(--radius-button)] transition-colors';

function btnCls(active: boolean, disabled: boolean, danger = false): string {
  if (disabled) return `${BTN_BASE} text-gray-argent cursor-not-allowed`;
  if (active) return `${BTN_BASE} border border-b-4 border-success bg-white text-success`;
  if (danger) return `${BTN_BASE} cursor-pointer text-danger hover:bg-gray-bright`;
  return `${BTN_BASE} cursor-pointer text-success hover:bg-gray-bright`;
}

const Sep = () => (
  <div className="mx-1.5 h-5 w-px self-center bg-gray-bright" />
);

// ─────────────────────────────────────────────────────────────
// MapToolbar
// Horizontal pill at the top of the map.
// Button groups (left → right):
//   [Center All · Center Selection · Deselect]
//   [New Boundary · Edit Boundary · Save]
//   [Merge Adjacent Boundaries · Split Boundary]
//   [Delete Boundary]
// ─────────────────────────────────────────────────────────────
const MapToolbar = ({
  mode,
  setMode,
  isLoading,
  selectedPaddock,
  paddocksToMerge,
  draftName,
  snapEnabled,
  onSnapToggle,
  onCenterAll,
  onCenterSelection,
  onRequestMergeConfirm,
  handleSavePaddock,
  handleDeletePaddock,
  handleRenamePaddock,
  handleUpdateArea,
  clearPaddockSelection,
  canUndo,
  canRedo,
  undoCount,
  redoCount,
  onUndo,
  onRedo,
  splitLineCount,
  onRequestSplitApply,
  cutoutVertexCount,
  onRequestCutoutFinalize,
  drawVertexCount,
  onRequestDrawFinalize,
}: {
  mode: PaddockMapMode;
  setMode: (mode: PaddockMapMode) => void;
  isLoading: boolean;
  selectedPaddock: MapFeature | null;
  paddocksToMerge: MapFeature[];
  onCenterAll: () => void;
  onCenterSelection: () => void;
  onRequestMergeConfirm: () => void;
  draftName: string;
  snapEnabled: boolean;
  onSnapToggle: () => void;
  handleSavePaddock: () => void;
  handleDeletePaddock: () => void;
  handleRenamePaddock: () => void;
  handleUpdateArea: () => void;
  clearPaddockSelection: () => void;
  canUndo: boolean;
  canRedo: boolean;
  undoCount: number;
  redoCount: number;
  onUndo: () => void;
  onRedo: () => void;
  splitLineCount: number;
  onRequestSplitApply: () => void;
  cutoutVertexCount: number;
  onRequestCutoutFinalize: () => void;
  drawVertexCount: number;
  onRequestDrawFinalize: () => void;
}) => {
  const hasGeometryChange = canUndo;
  const hasNameChange = selectedPaddock !== null && draftName.trim() !== selectedPaddock.name;
  const canSave = mode === 'edit' && !isLoading && draftName.trim() !== '' && (hasGeometryChange || hasNameChange);

  const isView   = mode === 'view';
  const isEdit   = mode === 'edit';
  const isDraw   = mode === 'draw';
  const isMerge  = mode === 'merge';
  const isSplit  = mode === 'split';
  const isCutout = mode === 'cutout';

  return (
    <div className="absolute left-3 top-3 z-10 flex items-center rounded-xl bg-white px-2 py-1.5 map-card-shadow">
      {/* ── Center All ────────────────────────────────────── */}
      <Tooltip title="Center All" placement="bottom">
        <button
          type="button"
          className={btnCls(false, false)}
          onClick={onCenterAll}
        >
          {ic(LuExpand)}
        </button>
      </Tooltip>

      {/* ── Center Selection + Deselect ───────────────────── */}
      {!isDraw && (
        <>
          <Tooltip title="Center Selection" placement="bottom">
            <button
              type="button"
              className={btnCls(false, !selectedPaddock)}
              disabled={!selectedPaddock}
              onClick={onCenterSelection}
            >
              {ic(LuCrosshair)}
            </button>
          </Tooltip>
          <Tooltip title="Deselect" placement="bottom">
            <button
              type="button"
              className={btnCls(false, !selectedPaddock || isLoading)}
              disabled={!selectedPaddock || isLoading}
              onClick={clearPaddockSelection}
            >
              {ic(LuXCircle)}
            </button>
          </Tooltip>
        </>
      )}

      <Sep />

      {/* ── New Boundary ──────────────────────────────────── */}
      {(isView || isDraw) && (
        <>
          <Tooltip
            title={
              isDraw ? (
                <span className="inline-flex items-center gap-1">
                  Cancel <ShortcutKey variant="light">Esc</ShortcutKey>
                </span>
              ) : (
                'New Paddock'
              )
            }
            placement="bottom"
          >
            <button
              type="button"
              className={btnCls(isDraw, isLoading)}
              onClick={() => isDraw ? setMode('view') : setMode('draw')}
              disabled={isLoading}
            >
              {isDraw ? ic(LuX) : ic(LuPlus)}
            </button>
          </Tooltip>
          {isDraw && drawVertexCount >= 3 && (
            <Tooltip
              title={
                <span className="inline-flex items-center gap-1">
                  Finish boundary{' '}
                  <ShortcutKey variant="light">Enter</ShortcutKey>
                </span>
              }
              placement="bottom"
            >
              <button
                type="button"
                className="ml-1 h-8 cursor-pointer rounded-lg bg-success px-3 text-xs font-semibold text-white transition-colors hover:bg-success-dark disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isLoading}
                onClick={onRequestDrawFinalize}
              >
                Finish ({drawVertexCount} pts)
              </button>
            </Tooltip>
          )}
        </>
      )}

      {/* ── Rename Paddock ────────────────────────────────── */}
      {isView && (
        <Tooltip title="Rename Paddock" placement="bottom">
          <button
            type="button"
            className={btnCls(false, !selectedPaddock || isLoading)}
            disabled={!selectedPaddock || isLoading}
            onClick={handleRenamePaddock}
          >
            {ic(LuTag)}
          </button>
        </Tooltip>
      )}

      {/* ── Update Area ───────────────────────────────────── */}
      {isView && (
        <Tooltip title="Update Area" placement="bottom">
          <button
            type="button"
            className={btnCls(false, !selectedPaddock || isLoading)}
            disabled={!selectedPaddock || isLoading}
            onClick={handleUpdateArea}
          >
            {ic(LuRuler)}
          </button>
        </Tooltip>
      )}

      {/* ── Edit Boundary ─────────────────────────────────── */}
      {(isView || isEdit) && (
        <Tooltip
          title={
            isEdit ? (
              <span className="inline-flex items-center gap-1">
                Cancel Edit <ShortcutKey variant="light">Esc</ShortcutKey>
              </span>
            ) : (
              'Edit Boundary'
            )
          }
          placement="bottom"
        >
          <button
            type="button"
            className={btnCls(isEdit, !selectedPaddock || isLoading)}
            disabled={!selectedPaddock || isLoading}
            onClick={() => {
              if (isEdit) clearPaddockSelection();
              else setMode('edit');
            }}
          >
            {isEdit ? ic(LuX) : ic(LuPencil)}
          </button>
        </Tooltip>
      )}

      {/* ── Undo / Redo / Save ────────────────────────────── */}
      {mode === 'edit' && (
        <div className="ml-1 flex items-center gap-3">
          <Tooltip
              title={
                <span className="inline-flex items-center gap-1">
                  Undo <ShortcutHint shortcutKey="Z" variant="light" />
                </span>
              }
              placement="bottom"
            >
            <div className="relative">
              <button
                type="button"
                className={btnCls(false, !canUndo || isLoading)}
                disabled={!canUndo || isLoading}
                onClick={onUndo}
              >
                {ic(LuUndo2)}
              </button>
              {undoCount > 0 && (
                <span className="pointer-events-none absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-white bg-danger px-0.5 text-[0.5625rem] font-bold leading-none text-white">
                  {undoCount > 99 ? '99+' : undoCount}
                </span>
              )}
            </div>
          </Tooltip>
          <Tooltip
              title={
                <span className="inline-flex items-center gap-1">
                  Redo{' '}
                  <ShortcutKey variant="light">Ctrl</ShortcutKey>
                  <span>/</span>
                  <ShortcutKey variant="light"><CmdKey /></ShortcutKey>
                  <span>+</span>
                  <ShortcutKey variant="light">Shift</ShortcutKey>
                  <span>+</span>
                  <ShortcutKey variant="light">Z</ShortcutKey>
                </span>
              }
              placement="bottom"
            >
            <div className="relative">
              <button
                type="button"
                className={btnCls(false, !canRedo || isLoading)}
                disabled={!canRedo || isLoading}
                onClick={onRedo}
              >
                {ic(LuRedo2)}
              </button>
              {redoCount > 0 && (
                <span className="pointer-events-none absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-white bg-danger px-0.5 text-[0.5625rem] font-bold leading-none text-white">
                  {redoCount > 99 ? '99+' : redoCount}
                </span>
              )}
            </div>
          </Tooltip>

          <button
            type="button"
            className="ml-1 h-8 rounded-[var(--radius-button)] px-3 text-xs font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer bg-success hover:bg-success-dark"
            disabled={!canSave}
            onClick={handleSavePaddock}
          >
            Save
          </button>
        </div>
      )}

      {(mode === 'edit' || mode === 'draw') && (
        <>
          <Sep />
          <Tooltip
            title={
              snapEnabled ? (
                <span className="flex flex-col items-start gap-0.5">
                  <span>Snap to vertices (on)</span>
                  <span className="inline-flex items-center gap-1">
                    <ShortcutKey variant="light">Alt</ShortcutKey> to override
                  </span>
                </span>
              ) : (
                'Snap to vertices (off)'
              )
            }
            placement="bottom"
          >
            <button
              type="button"
              className={btnCls(snapEnabled, false)}
              onClick={onSnapToggle}
            >
              {ic(LuMagnet)}
            </button>
          </Tooltip>
        </>
      )}

      {isView && <Sep />}

      {/* ── Merge ─────────────────────────────────────────── */}
      {(isView || isMerge) && (
        <>
          <Tooltip
            title={
              isMerge ? (
                <span className="inline-flex items-center gap-1">
                  Cancel Merge <ShortcutKey variant="light">Esc</ShortcutKey>
                </span>
              ) : (
                'Merge Adjacent Boundaries'
              )
            }
            placement="bottom"
          >
            <button
              type="button"
              className={btnCls(isMerge, isLoading || !selectedPaddock)}
              disabled={isLoading || !selectedPaddock}
              onClick={() => {
                if (isMerge) { setMode('view'); clearPaddockSelection(); }
                else setMode('merge');
              }}
            >
              {isMerge ? ic(LuX) : ic(LuGitMerge)}
            </button>
          </Tooltip>
          {isMerge && paddocksToMerge.length >= 2 && (
            <Tooltip title="Confirm — merge selected boundaries" placement="bottom">
              <button
                type="button"
                className="ml-1 h-8 cursor-pointer rounded-lg bg-success px-3 text-xs font-semibold text-white transition-colors hover:bg-success-dark disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isLoading}
                onClick={onRequestMergeConfirm}
              >
                Merge ({paddocksToMerge.length})
              </button>
            </Tooltip>
          )}
        </>
      )}

      {/* ── Split ─────────────────────────────────────────── */}
      {(isView || isSplit) && (
        <>
          <Tooltip
            title={
              isSplit ? (
                <span className="inline-flex items-center gap-1">
                  Cancel Split <ShortcutKey variant="light">Esc</ShortcutKey>
                </span>
              ) : (
                'Split Boundary'
              )
            }
            placement="bottom"
          >
            <button
              type="button"
              className={btnCls(isSplit, !selectedPaddock || isLoading)}
              disabled={!selectedPaddock || isLoading}
              onClick={() => {
                if (isSplit) { setMode('view'); clearPaddockSelection(); }
                else setMode('split');
              }}
            >
              {isSplit ? ic(LuX) : ic(LuScissors)}
            </button>
          </Tooltip>
          {isSplit && splitLineCount >= 1 && (
            <Tooltip
              title={
                <span className="inline-flex items-center gap-1">
                  Apply cuts{' '}
                  <ShortcutKey variant="light">Enter</ShortcutKey>
                </span>
              }
              placement="bottom"
            >
              <button
                type="button"
                className="ml-1 h-8 cursor-pointer rounded-lg bg-success px-3 text-xs font-semibold text-white transition-colors hover:bg-success-dark disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isLoading}
                onClick={onRequestSplitApply}
              >
                Apply ({splitLineCount} cut{splitLineCount > 1 ? 's' : ''})
              </button>
            </Tooltip>
          )}
        </>
      )}

      {/* ── Cutout ────────────────────────────────────────── */}
      {(isView || isCutout) && (
        <>
          <Tooltip
            title={
              isCutout ? (
                <span className="inline-flex items-center gap-1">
                  Cancel Cutout <ShortcutKey variant="light">Esc</ShortcutKey>
                </span>
              ) : (
                'Add Internal Cutout'
              )
            }
            placement="bottom"
          >
            <button
              type="button"
              className={btnCls(isCutout, !selectedPaddock || isLoading)}
              disabled={!selectedPaddock || isLoading}
              onClick={() => {
                if (isCutout) { setMode('view'); clearPaddockSelection(); }
                else setMode('cutout');
              }}
            >
              {isCutout ? ic(LuX) : ic(LuMinusSquare)}
            </button>
          </Tooltip>
          {isCutout && cutoutVertexCount >= 3 && (
            <Tooltip
              title={
                <span className="inline-flex items-center gap-1">
                  Finish cutout{' '}
                  <ShortcutKey variant="light">Enter</ShortcutKey>
                </span>
              }
              placement="bottom"
            >
              <button
                type="button"
                className="ml-1 h-8 cursor-pointer rounded-lg bg-success px-3 text-xs font-semibold text-white transition-colors hover:bg-success-dark disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isLoading}
                onClick={onRequestCutoutFinalize}
              >
                Finish ({cutoutVertexCount} pts)
              </button>
            </Tooltip>
          )}
        </>
      )}

      {/* ── Delete ────────────────────────────────────────── */}
      {isView && (
        <>
          <Sep />
          <Tooltip title="Delete Boundary" placement="bottom">
            <button
              type="button"
              className={btnCls(false, !selectedPaddock || isLoading, !!selectedPaddock && !isLoading)}
              disabled={!selectedPaddock || isLoading}
              onClick={handleDeletePaddock}
            >
              {ic(LuTrash2)}
            </button>
          </Tooltip>
        </>
      )}
    </div>
  );
};

export default MapToolbar;
